// src/encounter/lib/care-pathway.ts
//
// Care-pathway (Timeline) types + mapping for the encounter drawer's Timeline tab.
// Consumed by `care-pathway-timeline.tsx`.
//
// FLUTTER SOURCE (spec, read not edited):
//   encounter_module/encounter_base/data/datamodels/appointment_workflow_response.dart
//     → `WorkflowActivity { activityId, type, name, displayName, isCompleted, startedAt, … }`
//   encounter_module/encounter_base/presentation/widgets/timeline_tab.dart
//     → the row anatomy + the `leadingIcon()` type→glyph switch.
//
// ⚠️ FLUTTER STILL HAS NO FEED. `EncounterDataSourceImpl.fetchAppointmentWorkflow` is
// `return null;` (encounter_data_source_impl.dart:1116-1119) even though the use case /
// repository / DI wiring exist, so Flutter's `TimelineTab` ignores the response it is handed
// and renders a HARDCODED 2018 fixture (timeline_tab.dart:38-79) — those literal rows are what
// the live PA screenshot shows. That mock is deliberately NOT in this file: it lives in
// `src/encounter/fixtures/care-pathway.ts` so only stories can reach it.
//
// React now reads the REAL route — `useAppointmentWorkflow` →
// GET workflows/appointments/{id}/history (PROD-738) — and `toCarePathwayActivities()` below
// maps its wire shape onto `CarePathwayActivity`. No feed, a failed load and an empty pathway
// stay three distinct rendered states; nothing here ever falls back to the fixture.
import type { ActivityKind } from '@scripsteam/scrips-signal-ds';
import type { AppointmentWorkflowData } from '@/types/workflow-api';
import type { ScripsIconName } from '@scripsteam/scrips-signal-ds';
import { formatDate } from '@/shared/lib/date-format';

/**
 * One care-pathway activity — the React shape of Flutter's `WorkflowActivity`
 * (appointment_workflow_response.dart:126-199).
 */
export interface CarePathwayActivity {
  /** `WorkflowActivity.activityId` (or any stable key). */
  id: string;
  /** `WorkflowActivity.name` → the row title. */
  name: string;
  /** `WorkflowActivity.displayName` → the muted secondary line. */
  detail?: string;
  /** `WorkflowActivity.isCompleted` → completed (check) vs pending (clock) in Flutter. */
  completed?: boolean;
  /** Row glyph family. Derive from `WorkflowActivity.type` via `workflowActivityKind()`. */
  kind?: ActivityKind;
  /** Pre-formatted day marker (Flutter shows `scDateFormat(startedAt)` on each new day). */
  date?: string;
  /**
   * Raw `WorkflowActivity.type` (the Elsa activity type), carried through so a host can
   * resolve its OWN leading glyph. Two renderers need two glyph vocabularies from this one
   * field — the DS drawer wants `kind` (an `ActivityKind`), the hand-rendered pre-encounter
   * rail wants the Flutter icon-font codepoint (`workflowActivityIcon()`) — so the
   * discriminator travels rather than one of them guessing back from the other.
   */
  type?: string;
}

/**
 * Flutter `TimelineRow.leadingIcon()` (timeline_tab.dart:273-288) mapped onto the DS
 * `ActivityKind` union. Documented approximation where the DS union has no exact peer:
 * the questionnaire activity (Flutter `iconInfo`) and the unknown default (Flutter `add`)
 * both fold to `'task'`; the examination activity (Flutter `check`) folds to `'measurement'`.
 */
export function workflowActivityKind(type?: string): ActivityKind {
  switch (type) {
    case 'CheckIfDoctorIsAssignedActivity':
      return 'appointment';
    case 'CheckBackPainQuestionnaire':
      return 'task';
    case 'CheckCarePlanInitiatedActivity':
      return 'lifestyle';
    case 'CheckExaminationIsDoneActivity':
      return 'measurement';
    case 'OrderLaboratoryTests':
      return 'lab';
    default:
      return 'task';
  }
}

/**
 * Flutter `TimelineRow.leadingIcon()` (timeline_tab.dart:271-288) VERBATIM — the same switch,
 * onto the same icon-font codepoints, which `ScripsIcon` already carries (they were added to
 * `scrips-icon.tsx` for this feed and had no caller until now).
 *
 * This is the EXACT mapping, not the documented approximation `workflowActivityKind()` has to
 * make: `iconInfo`, `custom`, `check` and `add` are distinct glyphs here, where the DS
 * `ActivityKind` union has to fold three of them together. Note the live PA app renders `add`
 * for every row — its hardcoded mock carries no `type` at all — so a real feed is the first
 * thing that will ever exercise the other five branches.
 */
export function workflowActivityIcon(type?: string): ScripsIconName {
  switch (type) {
    case 'CheckIfDoctorIsAssignedActivity':
      return 'appointment';
    case 'CheckBackPainQuestionnaire':
      return 'iconInfo';
    case 'CheckCarePlanInitiatedActivity':
      return 'custom';
    case 'CheckExaminationIsDoneActivity':
      return 'check';
    case 'OrderLaboratoryTests':
      return 'lab';
    default:
      return 'add';
  }
}

/** Local calendar day of an ISO timestamp — Flutter `dateOnly()` (utils.dart:169). */
function dayKey(iso: string | null | undefined, fallback: Date): string {
  const d = iso ? new Date(iso) : fallback;
  const at = Number.isNaN(d.getTime()) ? fallback : d;
  return `${at.getFullYear()}-${at.getMonth()}-${at.getDate()}`;
}

/**
 * `AppointmentWorkflowData` (the `/history` response's `data` envelope) →
 * the rows both care-pathway surfaces render.
 *
 * Faithful to `timeline_tab.dart` on the row anatomy: title = `name`, subtitle =
 * `displayName`, check vs clock = `isCompleted` (:245-259).
 *
 * ── The day marker ─────────────────────────────────────────────────────────────────────
 * `formatDate(startedAt)` — 'MMM dd, yyyy', which is what Flutter's `scDateFormat` renders
 * by default (`utils.dart:176`) — on the FIRST row, and thereafter only where a row's OWN
 * `startedAt` lands on a different local calendar day from the row above it.
 *
 * THAT RULE, AS IMPLEMENTED BELOW, IS THE CONTRACT. Flutter is not. The Flutter apps are
 * being retired and are not maintained: a fix for this exact rule was written and then
 * CLOSED UNMERGED (`scrips_msp1_flutter_shared#2368`) because Flutter work does not ship.
 * `timeline_tab.dart` is history that explains the SHAPE of this function, not a target to
 * match — change the rule on its own merits, and do not "restore parity" with the Dart.
 *
 * What the Dart actually did (`timeline_tab.dart:113-121`), recorded accurately because the
 * note that shipped here with PROD-738 misread it:
 *
 *     bool showDate = false;
 *     if (index == 0) {
 *       showDate = true;
 *     } else if (item.startedAt != null) {
 *       showDate = dateOnly(item.startedAt ?? DateTime.now()) !=
 *                  dateOnly(items[index - 1].startedAt ?? DateTime.now());
 *     }
 *
 *   • The CURRENT row's `?? DateTime.now()` is DEAD CODE — the `else if` has already
 *     established `item.startedAt != null`.
 *   • Only the PREVIOUS row's fallback is live, and it is what made the Dart's output depend
 *     on the wall clock at render time.
 *   • Two undated rows NEVER REACH the comparison: `showDate` simply stays `false`. The old
 *     note claimed they "compare EQUAL" against a single shared `now` — they do not compare
 *     at all. The rendered outcome coincides, the mechanism does not, and the misreading hid
 *     the two cases below.
 *
 * The `a.startedAt != null` guard and the previous-row fallback below transcribe that Dart,
 * so its quirks reproduce HERE — measured, not inferred (probe run 2026-09-11):
 *
 *     [undated, today]                     → [—, —]                the DATED row LOSES its marker
 *     [undated, yesterday]                 → [—, 'Sep 10, 2026']   marked
 *     [Mar 1 09:00, undated, Mar 1 16:00]  → [d, —, d]             'Mar 01, 2026' twice, spurious
 *
 * The first and third flip with the calendar — run them on a different day and the markers
 * move — which is why matching the Dart exactly was never a coherent goal. They are left
 * as-is DELIBERATELY: this note is a documentation correction, and changing the rule is a
 * behaviour change that needs its own ticket and its own tests. Nothing here argues the
 * current output is RIGHT, only that Flutter is not the authority for deciding what right is.
 *
 * `now` is still captured once per call, but only for determinism — so one mapping pass
 * cannot straddle midnight and grade two rows against different "today"s. That is not the
 * mechanism the old note described.
 *
 * Where React diverges, deliberately: Flutter force-unwraps `name!` / `displayName!` /
 * `isCompleted!` and would throw on a null the backend is free to send (every field on the
 * wire is nullable). Absent values degrade to an empty title / no subtitle / not-completed
 * instead — a partially-populated activity still renders rather than blanking the rail.
 */
export function toCarePathwayActivities(
  data: AppointmentWorkflowData | null | undefined,
): CarePathwayActivity[] {
  // TODO(PROD-2117): `activities` is an assumed key AND an assumed casing — System.Text.Json's
  // default camelCase, taken from the contract and never seen on the wire (dev has never run a
  // workflow instance; publish is broken). If a real payload arrives PascalCase (`Activities`),
  // every row vanishes and the rail says "no care-pathway activity" — a wrong clinical
  // statement rather than a crash, so it will not announce itself.
  const rows = data?.activities ?? [];
  const now = new Date();

  return rows.map((a, i) => {
    const showDate =
      i === 0 || (a.startedAt != null && dayKey(a.startedAt, now) !== dayKey(rows[i - 1].startedAt, now));

    return {
      // `activityId` is the natural key; an index suffix is the fallback because the backend
      // types it nullable and Elsa can emit the same activity twice in one run.
      id: a.activityId ? `${a.activityId}-${i}` : `activity-${i}`,
      name: a.name ?? '',
      detail: a.displayName ?? undefined,
      // TODO(PROD-2117): unverified whether a real run sends `isCompleted` for a step it has
      // not reached yet, or omits it. Null currently reads as NOT completed (clock glyph);
      // confirm that against a real payload before the check marks are trusted clinically.
      completed: a.isCompleted ?? false,
      // TODO(PROD-2117): the five `type` strings both switches key on come from Flutter's
      // `leadingIcon()`, not from an observed response — a real Elsa run may well emit
      // fully-qualified type names. Unrecognised values fall silently to 'task' / the 'add'
      // glyph, so log the real values the first time this returns rows.
      kind: workflowActivityKind(a.type ?? undefined),
      type: a.type ?? undefined,
      // formatDate('') / formatDate(null) → '' → the marker simply doesn't render, which is
      // also what Flutter's `scDateFormat(null)` produces.
      date: showDate ? formatDate(a.startedAt) || undefined : undefined,
    };
  });
}
